import React, { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useInfiniteQuery, useQuery } from 'react-query'
import { format, formatDistanceToNowStrict, isPast, isToday } from 'date-fns'
import { Loader2 } from 'lucide-react'

import { PageHeader } from '@/components/shared'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import JqlFilterBar, { QUICK_FILTERS } from '@/components/ProjectCentral/JqlFilterBar'
import SavedFiltersMenu from '@/components/ProjectCentral/SavedFiltersMenu'
import PriorityBadge from '@/components/ProjectCentral/PriorityBadge'
import TaskTypeIcon from '@/components/ProjectCentral/TaskTypeIcon'
import { LabelChip } from '@/components/ProjectCentral/LabelsInput'
import { taskService } from '@/services/taskService'
import { savedFilterService } from '@/services/savedFilterService'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 50
const DEFAULT_JQL = QUICK_FILTERS[0].jql

const personName = (u) => (u?.employee ? `${u.employee.first_name} ${u.employee.last_name || ''}`.trim() : u?.email)
const categoryStyle = {
  todo: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  done: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
}

// /projects/issues — search every project you can see with JQL; save searches as filters.
// The URL holds the query (?jql=…) or a saved filter (?filter=12), so results can be shared.
const IssueSearch = () => {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const filterId = params.get('filter')

  const { data: filtersData } = useQuery('saved-filters', savedFilterService.list)
  const currentFilter = useMemo(
    () => (filtersData?.data?.filters || []).find(f => String(f.id) === filterId) || null,
    [filtersData, filterId]
  )
  const jql = params.get('jql') ?? currentFilter?.jql ?? (filterId ? null : DEFAULT_JQL)

  const search = useInfiniteQuery(
    ['jql-search', jql],
    ({ pageParam = 0 }) => taskService.searchJql(jql, { offset: pageParam, limit: PAGE_SIZE }),
    {
      enabled: jql !== null,
      retry: false,
      keepPreviousData: true,
      getNextPageParam: (last) => {
        const { offset, tasks, total } = last.data
        const next = offset + tasks.length
        return next < total ? next : undefined
      }
    }
  )
  const tasks = (search.data?.pages || []).flatMap(p => p.data.tasks)
  const total = search.data?.pages?.[0]?.data?.total ?? 0
  const error = search.error?.response?.data?.error || (search.error ? 'Search failed' : null)

  const runQuery = (next) => {
    const nextParams = {}
    if (currentFilter && next === currentFilter.jql) nextParams.filter = String(currentFilter.id)
    else nextParams.jql = next
    setParams(nextParams)
  }
  const selectFilter = (filter) => setParams(filter ? { filter: String(filter.id) } : { jql: jql || DEFAULT_JQL })

  return (
    <div className="space-y-4">
      <PageHeader
        title={currentFilter ? currentFilter.name : 'Search issues'}
        description={currentFilter?.description || (currentFilter
          ? `Saved filter${currentFilter.mine ? '' : ` by ${currentFilter.owner?.name}`}`
          : 'Find tickets across all your projects with JQL, like in Jira. Save a search to reuse or share it.')}
        actions={<SavedFiltersMenu jql={jql || ''} current={currentFilter} onSelect={selectFilter} />}
      />

      <JqlFilterBar value={jql || ''} onSearch={runQuery} error={error} loading={search.isFetching} />

      <div className="text-sm text-muted-foreground flex items-center gap-2">
        {search.isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {!error && search.data && <span>{total} {total === 1 ? 'issue' : 'issues'}</span>}
      </div>

      {!error && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Key</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map(t => {
                const due = t.due_date ? new Date(`${t.due_date}T00:00:00`) : null
                const overdue = due && isPast(due) && !isToday(due) && t.status?.category !== 'done'
                return (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => navigate(`/projects/${t.project_id}/tasks/${t.id}`)}>
                    <TableCell>
                      <span className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground whitespace-nowrap">
                        <TaskTypeIcon type={t.type} /> {t.key}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[420px]">
                      <span className="block truncate font-medium text-sm" title={t.title}>{t.title}</span>
                      <span className="flex items-center gap-1 flex-wrap mt-0.5">
                        <span className="text-[11px] text-muted-foreground">{t.project?.name}</span>
                        {t.parent && <span className="text-[11px] text-muted-foreground">· ↳ {t.project?.key}-{t.parent.task_number}</span>}
                        {(t.labels || []).slice(0, 3).map(label => <LabelChip key={label} label={label} className="text-[10px]" />)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={cn('rounded px-1.5 py-0.5 text-xs whitespace-nowrap', categoryStyle[t.status?.category] || categoryStyle.todo)}>
                        {t.status?.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      {t.assignee ? (
                        <span className="flex items-center gap-1.5 text-sm whitespace-nowrap">
                          <Avatar className="h-5 w-5"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{personName(t.assignee).charAt(0).toUpperCase()}</AvatarFallback></Avatar>
                          {personName(t.assignee)}
                        </span>
                      ) : <span className="text-sm text-muted-foreground">Unassigned</span>}
                    </TableCell>
                    <TableCell><PriorityBadge priority={t.priority} /></TableCell>
                    <TableCell className={cn('text-sm whitespace-nowrap', overdue && 'text-destructive font-medium')}>
                      {due ? format(due, 'MMM d, yyyy') : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNowStrict(new Date(t.updated_at), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                )
              })}
              {!search.isLoading && tasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No issues match this search.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {search.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => search.fetchNextPage()} disabled={search.isFetchingNextPage}>
            {search.isFetchingNextPage ? 'Loading…' : `Load more (${total - tasks.length} left)`}
          </Button>
        </div>
      )}
    </div>
  )
}

export default IssueSearch
